import React, { useState } from 'react';
import { FaEnvelope, FaCheck, FaSpinner } from 'react-icons/fa';
import { emailSignup, trackEvent } from '../api/client';
import { getPendingTripContext, setPendingTripContext } from '../utils/tripContext';

/**
 * Email-first account capture.
 *
 * The visitor has just watched a real plan appear. The cheapest possible way to
 * keep it should be typing one thing — their email address. No password, no
 * confirmation field, no full name: the account is created behind the scenes on
 * the demo tier and a sign-in link does the rest.
 *
 * That inverts the usual order. An account stops being the barrier in front of
 * the work and becomes a by-product of saving it.
 *
 * @param {string} destination Where the plan is for — shown in the email, and
 *                             carried into the itinerary when they come back.
 * @param {string} surface     Analytics label for where this was mounted.
 */
const SavePlanByEmail = ({ destination, surface = 'preview', className = '' }) => {
  const [email, setEmail] = useState('');
  const [state, setState] = useState('idle'); // 'idle' | 'sending' | 'sent'
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    const address = email.trim();
    // Deliberately the loosest possible check. The server validates properly;
    // this only exists to catch an obvious slip before a round trip.
    if (address.length < 5 || !address.includes('@')) {
      setError('Enter an email address so we know where to send it.');
      return;
    }
    setError('');
    setState('sending');
    try {
      // Persist the trip alongside the address, so whatever they picked on a
      // booking block is still theirs when the sign-in link brings them back.
      setPendingTripContext({ destination, source: surface });
      const ctx = getPendingTripContext() || {};
      await emailSignup(address, {
        destination: destination || ctx.destination || null,
      });
      trackEvent('preview_saved_by_email', { surface, destination });
      setState('sent');
    } catch (err) {
      setState('idle');
      setError(
        err?.response?.data?.detail ||
          'Could not save that just now. Please try again.'
      );
    }
  };

  if (state === 'sent') {
    return (
      <div
        className={`my-8 rounded-xl border border-green-200 bg-green-50 px-5 py-4 ${className}`}
      >
        <div className="flex items-start gap-3">
          <span className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-green-100">
            <FaCheck className="text-green-600" />
          </span>
          <div>
            <h3 className="font-semibold text-green-800">Saved — check your email</h3>
            <p className="mt-1 text-sm leading-relaxed text-green-700">
              We sent a link to <span className="font-medium">{email.trim()}</span>. Open it
              and your {destination ? `${destination} ` : ''}plan will be waiting. There is no
              password to set up.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`my-8 rounded-xl border border-neutral-200 bg-white px-5 py-5 shadow-sm ${className}`}
    >
      <h3 className="font-serif text-lg font-bold leading-snug text-neutral-900">
        Keep this plan
      </h3>
      <p className="mt-1 text-[0.95rem] leading-relaxed text-neutral-600">
        Add your email and we'll save it to your account. No password, no card —
        we'll send a link that signs you straight in.
      </p>

      <form onSubmit={handleSubmit} className="mt-4 flex flex-wrap items-center gap-2">
        <label className="sr-only" htmlFor={`save-plan-email-${surface}`}>
          Email address
        </label>
        <div className="relative min-w-[14rem] flex-1">
          <FaEnvelope className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input
            id={`save-plan-email-${surface}`}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
            className="w-full rounded-lg border border-neutral-300 py-2.5 pl-9 pr-3 text-sm text-neutral-800 focus:outline-none focus:ring-2 focus:ring-primary-400"
          />
        </div>
        <button
          type="submit"
          disabled={state === 'sending'}
          className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700 disabled:opacity-60"
        >
          {state === 'sending' ? (
            <>
              <FaSpinner className="animate-spin" /> Saving…
            </>
          ) : (
            'Save my plan'
          )}
        </button>
      </form>

      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
    </div>
  );
};

export default SavePlanByEmail;
